// import { ref } from 'vue'
import { storeToRefs } from "pinia"
import { i18n } from "./../i18n"
import { orderFiles } from "./../lib/fileOrder"
import { keep, drop } from "./../lib/handoff"
import { isAnimatedGif } from "./../lib/gifFrames"
import { looksLikeSvg, rasteriseSvg } from "./../lib/svgRaster"
import { looksLikeHeic, heicToJpeg } from "./../lib/heicDecode"
import { looksLikeZip, expandZip } from "./../lib/zipIntake"
import { looksLikeVideo, decodeFrames, sampleStrip, delayForFps,
         DEFAULT_WIDTH, DEFAULT_FPS, MAX_SECONDS } from "./../lib/videoFrames"
import { createGifSink, gifNameFor } from "./../lib/gifAssemble"
/* The SAME renderer the meme page uses. layoutCaption() wraps and shrinks
 * to fit, which "a few words" needs on a 320-pixel GIF, and writing a
 * second caption drawer for frames is the divergence lib/meme.js exists to
 * prevent. usableMeme() validates the shape so the contract is shared too. */
import { drawMeme, usableMeme } from "./../lib/imageTransform"
import { MEME_SLOTS, memeStoreKeys, memePayload } from "./../lib/meme"
import { inputRules, inputVoice, taskInput, namedByAttr } from "./../lib/task"
import { track, resetFunnel, formatOfAll, totalBytes } from "./../lib/analytics"
import { useUiElements } from "./../store/uiElements"

// The catalogue, not a hard-coded sentence.
//
// errors.tooManyFiles, errors.notAnImage and errors.skippedNonImages have been
// translated into all ten languages since the catalogues were written, and
// were referenced by nothing: every alert below was an English string literal.
// So a French visitor who picked thirty files was told about it in English,
// and ten translations sat unused. The global instance rather than useI18n()
// because addFiles() is reached from event handlers, not only from setup.
const t = (key, args) => i18n.global.t(key, args || {})

/* The clip the staged GIF was made from, and which part of it was used.
 *
 * BOTH have to live out here, past $reset(). convertVideo() opens with a reset
 * and then wanted uiStore.videoStart/videoEnd -- which the reset had just put
 * back to 0, so `to` came out null and every conversion took the whole clip
 * however the handles were placed. The trimmer said 0:04 selected and the GIF
 * came back with all 100 frames.
 *
 * The staging path has the same hazard from the other end: addFiles() resets
 * too, so a selection kept only in the store would not survive being handed
 * the result, and changing frames-a-second afterwards would silently
 * re-convert the whole clip. */
/* HOW THE FILE ARRIVED, and whether this call is the visitor's or ours.
 *
 * Both have to be module-scope for the reason lastClip is: addFiles() opens
 * with $reset(), and neither of these belongs in the store.
 *
 * `entryMethod` is set by the caller a moment before it calls, because
 * addFiles() is bound STRAIGHT to the drop zone's event (App.vue:114) and
 * cannot be given an argument there without changing what the template does.
 *
 * `fromConverter` is the more interesting one, and it is named for WHO CALLED
 * rather than for what it permits. There was once a `derived` flag in this
 * file that let a caller put a file PAST the intake policy; it was removed,
 * and tests/file_intake_test.mjs forbids that identifier here by name so it
 * cannot quietly come back. This one grants nothing and refuses nothing --
 * every file still goes through every check below, whoever handed it over. It
 * decides exactly one thing: whether an event has already been counted.
 *
 * addFiles() RE-ENTERS ITSELF: a clip,
 * an SVG and a HEIC are each converted and the result is handed back through
 * this same door, which is what lets the stage, the width control and the
 * results table work with no knowledge that a conversion happened. It also
 * means one human action calls addFiles() twice, and an event fired naively
 * would count the drop twice and report the SECOND format -- png for an SVG,
 * jpeg for a HEIC, gif for a video. The first call is the honest one, so the
 * three converters set this before handing their output back. */
let entryMethod = null
let fromConverter = false

let lastClip = null
let lastRange = null
/* The SVG the staged PNG was rasterised FROM. Same reasoning as lastClip: by
 * the time the PNG is on the stage the drawing has left it, so changing the
 * background afterwards would have nothing to re-rasterise. Dropped as soon
 * as anything arrives that did not come from the SVG converter. */
let lastDrawing = null

/* THE FILE A CONVERSION REPLACED, kept so a page can still see the original.
 *
 * A HEIC is decoded on the device and re-enters addFiles() as a JPEG, which
 * is right for every tool that makes a picture -- but libheif writes that
 * JPEG with NO EXIF, so /inspect-metadata was reading the conversion and
 * reporting "Nothing found in this one" about an iPhone photograph carrying
 * GPS coordinates. A false negative, on the one page where a false negative
 * is dangerous: somebody checks, is reassured, and shares it.
 *
 * Module scope for the reason lastClip and lastDrawing are: addFiles() opens
 * with $reset(), so anything that must survive staging is written afterwards
 * and cannot live in the store. A WeakMap because the key is the staged File
 * and nothing should keep either alive once the page has let go.
 */
const originals = new WeakMap()
export function originalOf(file) {
	return (file && originals.get(file)) || null
}
/* Which background is IN the staged PNG. Without it, picking the colour
 * already showing re-rasterises for a byte-identical file -- the same waste
 * `bakedCaption` exists to prevent on the video path. */
let lastBackground = null
/* And how many pixels across it was drawn, 0 meaning the automatic 2x. Beside
 * lastBackground for the same reason: re-picking the width already showing
 * must not re-rasterise for a byte-identical file. */
let lastSvgWidth = 0
/* Which caption is currently IN the staged bytes, or null for none. Module
 * scope with the other two, because $reset() runs between the bake and the
 * question "is it already baked?". */
let bakedCaption = null

// The refusal has to name what the page wanted. "Doesn't look like a valid
// IMAGE file" on a page whose whole subject is a document says the wrong noun
// at the one moment the visitor needs the right one. Falls back to .images for
// a voice with no strings written yet, so a new task shape never renders a raw
// key path at somebody.
const says = (base, args) => {
	const key = `${base}.${inputVoice(batchCapacity())}`
	return i18n.global.te(key) ? t(key, args) : t(`${base}.images`, args)
}

/* How many files THIS page takes.
 *
 * A cap is a property of the task, not of the application: /pdf-to-jpg takes
 * one document, and always did -- startPdf() picks the first PDF and ignores
 * any others, which is a silent wrong answer rather than a limit. The registry
 * says so now, and MAX_BATCH_FILES is what a page gets for not saying. */
export function batchCapacity() {
    return taskInput().max || MAX_BATCH_FILES
}

// A ceiling, not a guess. Every image is one upload to Flask plus one transform
// on the CDN, so a large selection is slower in proportion and a very large one
// would look broken rather than slow.
//
// Raised from ten to twenty for the assembled PDF: people putting scanned pages
// back together routinely have fifteen or more, and ten was the limit most
// likely to disappoint them. Twenty is safe now that the upload phase is run
// through inBatches() like the CDN phase already was -- before that it was an
// unbounded Promise.all, which on HTTP/2 means every file at once rather than
// the six a browser used to impose.
export const MAX_BATCH_FILES = 20

/* One PDF is worth many images, so it is the one non-image this accepts.
 *
 * The extension is checked as well as the type because browsers are
 * inconsistent about what they report for a file dragged from some file
 * managers, and the server sniffs the real magic bytes anyway -- this only
 * decides whether to bother sending it. */
export function isPdf(file) {
	if (!file) return false
	if ((file.type || '').toLowerCase() === 'application/pdf') return true
	return /\.pdf$/i.test(file.name || '')
}

// Revoke everything a stashed batch was holding. Object URLs are not garbage
// collected: dropping the last reference to one leaks it until the page closes.
export function releaseStashedBatch(stash) {
	if (!stash) return
	for (const item of stash.batchItems || []) {
		if (item.blob) window.URL.revokeObjectURL(item.blob)
		for (const v of item.versions || []) {
			if (v.blob) window.URL.revokeObjectURL(v.blob)
		}
	}
}

export default function () {


	const uiStore = useUiElements()
	const {files} = storeToRefs(uiStore)

	/* Everything staged here came from the visitor, so the intake policy is
	 * unconditional.
	 *
	 * It used to take a `derived` flag that switched most of it off, for one
	 * caller: "open on its own" pulled a rendered page back off the CDN and
	 * re-staged it, and on /pdf-to-jpg that is a JPEG arriving at a drop zone
	 * declaring it takes a PDF -- the application's own output refused by its
	 * own policy. That caller is gone: the row opens the page in a tab now
	 * rather than feeding it back in. Nothing else was ever exempt -- a
	 * clipboard paste is the visitor handing over something new, and stayed
	 * policed even then. */
	function addFiles(newFiles) {

		/* THE CLIP IS FORGOTTEN HERE, UNCONDITIONALLY, and convertVideo()
		 * puts it back afterwards.
		 *
		 * There is deliberately no "this one is ours" argument. addFiles()
		 * took a `derived` flag once, to let the application re-stage its own
		 * output past the policy, and it was removed because a switch that
		 * turns intake checks off is not worth keeping --
		 * tests/file_intake_test.mjs holds it gone. So the GIF this page makes
		 * is admitted by the page's DECLARED OUTPUT instead
		 * (KIND_RULES.video.produces), which a caller cannot set.
		 *
		 * Clearing unconditionally and restoring after is the same idiom the
		 * truncation notice uses: $reset() below wipes the store, so anything
		 * that must survive staging is written once staging is done. */
		lastClip = null
		lastRange = null
		lastSignature = null
		bakedCaption = null
		lastDrawing = null
		lastBackground = null
		lastSvgWidth = 0

		/* THE FUNNEL STARTS HERE, before the conversions and before the
		 * refusals, and both of those placements are deliberate.
		 *
		 * Before the CONVERSIONS, because a clip, an SVG and a HEIC re-enter
		 * this function as a GIF, a PNG and a JPEG -- so anything reported
		 * further down would name the format this application made rather
		 * than the one the visitor had.
		 *
		 * Before the REFUSALS, because choosing a file IS the act being
		 * measured. A drop that is turned away is a visitor who wanted
		 * something and did not get it, which is the most useful row in the
		 * report: it pairs with the optimization_error fired beside each
		 * alert, and the difference between the two counts is the conversion
		 * rate of the intake itself. */
		const chosen = [...newFiles]
		if (!fromConverter) {
			resetFunnel()
			track("file_selected", {
				file_count: chosen.length,
				input_format: formatOfAll(chosen),
				total_input_bytes: totalBytes(chosen),
				entry_method: entryMethod || "unknown",
			})
		}
		/* One call, one answer. Cleared immediately so a later programmatic
		 * intake -- a carry-over, a sample -- cannot inherit the label of the
		 * gesture before it. */
		entryMethod = null
		fromConverter = false

		/* A VIDEO IS CONVERTED BEFORE IT IS INTAKE AT ALL.
		 *
		 * Here rather than in the drop handler, because addFiles() is the one
		 * door and a second intake is what the URL-fetch path was doing wrong.
		 * The clip re-enters through this same function as an ordinary GIF,
		 * so the notice, f_agif, the width cap and the results table all apply
		 * with no knowledge that a video was ever involved.
		 *
		 * Only where the page asked for video. Everywhere else a .mp4 falls
		 * through to the refusal below and is told it is not an image, which
		 * is true of every other page on the site.
		 *
		 * Synchronous return, asynchronous work: convertVideo() takes seconds
		 * and calls addFiles() itself when it has a GIF. Nothing on this path
		 * auto-uploads, and the one caller that follows addFiles() with
		 * startUploadFiles() -- the share hand-off -- now CAN carry a video,
		 * since the manifest offers this app for video shares too. It asks
		 * looksLikeVideo() first and skips the upload for a clip, because the
		 * clip is in the trimmer rather than on the stage and there would be
		 * nothing to send. */
		if (taskInput().kind === "video") {
			const clips = [...newFiles].filter(looksLikeVideo)
			if (clips.length) {
				if (clips.length > 1) {
					track("optimization_error", {error_type: "too_many_for_this_page",
						input_format: formatOfAll(clips), file_count: clips.length})
					alert(t("errors.onlyOne", {count: clips.length}))
					return
				}
				startTrim(clips[0])
				return
			}
		}

		/* AN SVG IS RASTERISED BEFORE IT IS INTAKE, for the same reason and by
		 * the same route as a clip: it re-enters through this one door as an
		 * ordinary PNG, so the stage, the width control, the results table and
		 * the CDN all apply with no knowledge that a vector was involved.
		 *
		 * THE CDN CANNOT DO THIS ONE. ImageEngine's documentation is explicit
		 * that SVG is "not resized or converted, just optimized and
		 * compressed", so /svg-to-png is the only conversion on this site that
		 * is not a directive. It is a canvas, on the device -- which also
		 * means the SVG itself never reaches S3, where today it would be
		 * stored and served through the CDN with any <script> it carries.
		 *
		 * Only where the page asked for one. Everywhere else an SVG stays an
		 * SVG and is optimised, which is the existing behaviour and is fine.
		 *
		 * No re-conversion path, unlike a clip: the PNG is drawn at twice the
		 * picture's natural size and every width the visitor then asks for is
		 * a `w_` directive on that, so nothing here has to run twice. */
		/* A HEIC IS DECODED BEFORE IT IS INTAKE, and for once nothing else
		 * could have done it. The browser will not decode HEIC (only Safari
		 * will) and ImageEngine returns it untouched -- measured, every
		 * directive gives back `origin-object`. So the decoder ships with the
		 * page; see lib/heicDecode.js.
		 *
		 * It re-enters through this one door as an ordinary JPEG, so the
		 * stage, the width control, the other formats and the results table
		 * all apply with no knowledge that a HEIC was ever involved -- and
		 * every other format the visitor asks for is then made by the CDN
		 * from that JPEG. Two lossy steps, accepted knowingly until the
		 * engine supports HEIC natively.
		 *
		 * WHEREVER PICTURES ARE TAKEN, not only on the page named after the
		 * format. Reported from /compress-photos-for-email -- "Sorry. Doesn't
		 * look like a valid IMAGE file." -- which is exactly the page a
		 * visitor with a phone full of holiday photographs arrives at, and
		 * the refusal was ours rather than the file's.
		 *
		 * The old gate was `taskInput().kind === "heic"`, written when the
		 * conversion was believed to be ImageEngine's job and so had to be
		 * asked for. It is the browser's job now, the decoder is already in
		 * the bundle, and a photograph is a photograph on every page that
		 * takes one. `rules.images` is the right question: a clip page and a
		 * document page both answer no and still refuse it, which they
		 * should -- neither has anything to do with a still photograph. */
		/* A ZIP IS OPENED BEFORE IT IS INTAKE, and it goes FIRST of the
		 * conversions because it is the only one that can contain the others.
		 *
		 * Reported while photographing slides at a conference: the set was
		 * selected in Google Photos, which hands back a .zip, and the page whose
		 * own headline is "Doing a folder of photos one at a time is the worst
		 * part of this job" answered "Doesn't look like a valid IMAGE file."
		 *
		 * The pictures re-enter through this same door, which is the whole
		 * payoff: a zip full of iPhone .heic is decoded by the branch below on
		 * the way back in, with nothing here knowing that is what it holds, and
		 * the ordering, the cap, the previews and the output zip all apply as
		 * though the files had been dropped by hand. No exemption flag -- the
		 * archive is admissible because KIND_RULES says this page takes one.
		 *
		 * Only where the page takes pictures, the same question the HEIC branch
		 * asks: a clip page and a document page both answer no and still refuse
		 * it, which is right for both. */
		if (inputRules().images) {
			const archives = [...newFiles].filter(looksLikeZip)
			if (archives.length) {
				expandZips(archives, [...newFiles].filter((f) => !looksLikeZip(f)))
				return
			}
		}

		if (inputRules().images) {
			const shots = [...newFiles].filter(looksLikeHeic)
			if (shots.length) {
				convertHeic(shots, [...newFiles].filter((f) => !looksLikeHeic(f)))
				return
			}
		}

		/* WHEREVER PICTURES ARE TAKEN, for the reason the HEIC branch above
		 * gives and for one more that is specific to this format.
		 *
		 * Reported from the HOME page: a drawing was accepted, uploaded, and
		 * came back reading "100% smaller -- 985 Bytes original, 0 Bytes
		 * after optimising". Measured, that is exactly what happens. The
		 * engine does not convert an SVG (its own documentation: "SVG files
		 * are not resized or converted"), so `?imgeng=/w_640/f_jpeg` returned
		 * the SVG unchanged, `image/svg+xml`, and the application had no
		 * raster to measure -- which it rendered as a triumphant zero.
		 *
		 * The conversion has to happen HERE, in the browser, or it does not
		 * happen at all. So the question is only whether the page asked for
		 * it by name, and a page that takes pictures has effectively asked.
		 * A clip page and a document page still answer no. */
		if (inputRules().images) {
			const drawings = [...newFiles].filter(looksLikeSvg)
			if (drawings.length) {
				/* ONE AT A TIME, still. Rasterising several means several
				 * full-size canvases alive at once, and the width that suits
				 * one drawing rarely suits the next. A page that takes twenty
				 * photographs may take one drawing among them. */
				if (drawings.length > 1) {
					track("optimization_error", {error_type: "too_many_for_this_page",
						input_format: formatOfAll(drawings), file_count: drawings.length})
					alert(t("errors.onlyOne", {count: drawings.length}))
					return
				}
				/* The rest of the drop travels with it. On a batch page a
				 * mixed handful is a normal thing to drop, and the earlier
				 * version returned here and silently discarded every
				 * photograph beside the drawing. */
				const rest = [...newFiles].filter((f) => !looksLikeSvg(f))
				convertSvg(drawings[0], uiStore.svgBackground, rest)
				return
			}
		}

		// REFUSED BEFORE ANYTHING IS TOUCHED.
		//
		// Order matters more than it looks: every refusal below used to sit
		// AFTER $reset(), so dropping a stray .txt on a staged image lost the
		// image as well as the drop -- an apology for the file you did not
		// want, plus the silent removal of the one you did.
		//
		// A page that takes one file REFUSES a second rather than keeping the
		// first, and the distinction is not fussiness. The twenty-file cap is
		// about QUANTITY: any twenty of thirty are as good as any other twenty,
		// so taking some and saying so loses nothing. One-of-many is about
		// IDENTITY: picking for the visitor means picking WRONG most of the
		// time, and silently.
		const cap = batchCapacity()

		// Ordered BEFORE the cap is applied, so somebody who picks twenty-five
		// scans gets pages one to twenty rather than an arbitrary twenty. The
		// order then carries all the way through: the previews, the rows in the
		// results table, and the page order of the assembled PDF. See
		// lib/fileOrder.js for the rule.
		const offered = orderFiles([...newFiles])

		// A page that takes ONE file refuses a second rather than keeping the
		// first, and the distinction is not fussiness. The twenty-file cap is
		// about QUANTITY: any twenty of thirty are as good as any other twenty,
		// so taking some and saying so loses nothing. One-of-many is about
		// IDENTITY: choosing for the visitor means choosing WRONG most of the
		// time, and silently.
		if (cap === 1 && offered.length > 1) {
			track("optimization_error", {error_type: "too_many_for_this_page",
				input_format: formatOfAll(offered), file_count: offered.length})
			alert(t("errors.onlyOne", {count: offered.length}))
			return
		}

		let filesToUpload = offered.slice(0, cap)

		// Non-images are dropped rather than refused outright: selecting twelve
		// photos and one stray file should optimize the twelve, not abandon the
		// lot. A single bad file is still worth saying out loud.
		//
		// A PDF is accepted, and is the one case that is not an image: it is
		// rasterised on the server into one page per image. See startPdf() in
		// App.vue and pdf_pages.py.
		// The same table the accept attribute is built from, so the picker
		// and the code cannot disagree about what this page takes.
		const rules = inputRules()
		const images = filesToUpload.filter((f) => isPdf(f)
			? rules.pdf
			// rules.produces is what THIS PAGE MAKES, declared beside what it
			// takes. /mp4-to-gif takes a clip and produces an image/gif, so a
			// GIF is admissible there and every other image is still refused
			// -- which is how the page can stage its own output without an
			// exemption anybody can pass in. See KIND_RULES in lib/task.js.
			: (rules.images || (rules.produces && (f.type || '') === rules.produces))
				// namedByAttr: a .heic off Windows, a .svgz, anything out of a
				// zip -- file.type is the OS's extension map and is absent
				// often enough to matter. A page that LISTED the extension in
				// its own accept attribute should not then refuse the file its
				// own picker offered. Pages whose attr is only `image/*` list
				// no extensions and are unaffected.
				&& ((f.type || '').includes('image') || namedByAttr(f, rules.attr)))
		if (!images.length) {
			/* The one refusal that means "this page does not take that". It is
			 * the row to watch per landing page: a lot of these on one tool is
			 * either a picker offering too much or copy promising too much. */
			track("optimization_error", {error_type: "format_not_accepted",
				input_format: formatOfAll(filesToUpload), file_count: filesToUpload.length})
			alert(says("errors.notAcceptable"))
			return
		}

		// NOTHING ABOVE THIS LINE TOUCHES THE STORE, and that is the fix rather
		// than a tidy-up. Every refusal used to sit after $reset(), so dropping a
		// stray .txt onto a staged image lost the image as well as the drop: an
		// apology for the file you did not want, and the silent removal of the
		// one you did. A refusal now costs the visitor nothing.
		//
		// A batch set aside by "open on its own" is abandoned the moment new
		// files are chosen, so its object URLs are released here -- the one place
		// every entry point (drop, picker, paste, share, URL fetch) passes
		// through. $reset() would drop the reference and leak them.
		//
		// openRemotePage() assigns the stash AFTER calling this, so opening a
		// page does not release the batch it came from.
		releaseStashedBatch(uiStore.stashedBatch)
		uiStore.$reset()
		removeAllChildNodes(document.getElementById("fetchedUrlPreview"))

		if (offered.length > cap) {
			// silently keeping the first twenty would look like a bug
			track("optimization_error", {error_type: "over_batch_cap",
				input_format: formatOfAll(offered), file_count: offered.length})
			alert(t("errors.tooManyFiles", {count: offered.length, max: cap}))
		}
		if (images.length < filesToUpload.length) {
			track("optimization_error", {error_type: "some_files_skipped",
				input_format: formatOfAll(filesToUpload),
				file_count: filesToUpload.length - images.length})
			alert(t("errors.skippedNonImages", {n: filesToUpload.length - images.length}))
		}

		let newUploadableFiles = images.map((file) => new UploadableFile(file)).filter((file) => !fileExists(file.id))
		files.value = files.value.concat(newUploadableFiles)
		uiStore.batchMode = files.value.length > 1
		uiStore.showAddSetting = true
		uiStore.showFetchedImagePreviewArea = false

		/* Put the picture aside so that switching tools does not lose it.
		 *
		 * HERE, and not at the seven call sites, because this is the one door:
		 * drop, picker, paste, camera, sample, blank starter, URL fetch and an
		 * Android share all arrive through addFiles(). A carry-over that only
		 * covered some of them would be a feature that works depending on how
		 * you got here.
		 *
		 * Not awaited. It is a convenience for the NEXT page load, so making
		 * this one wait on a disk write to show a preview would be the wrong
		 * way round, and lib/handoff.js swallows its own failures.
		 */
		keep(files.value.length === 1 ? files.value[0].file : null)

		/* Say so when a GIF moves.
		 *
		 * Every output format here is a still, so an animation converted to
		 * one keeps its first frame and drops the rest. That may be exactly
		 * what somebody wanted -- a frame out of a reaction GIF is a normal
		 * thing to want -- so this is a notice and not a refusal.
		 *
		 * Asynchronous, and guarded by the id it was asked about: reading the
		 * bytes takes a tick, and a second drop landing inside that tick would
		 * otherwise put the previous file's answer on the new one.
		 */
		const first = files.value.length === 1 ? files.value[0] : null
		if (first) {
			const asked = first.id
			isAnimatedGif(first.file).then((animated) => {
				if (!animated) return
				const now = files.value.length === 1 ? files.value[0] : null
				if (!now || now.id !== asked) return
				uiStore.animatedGif = true
			})
		}
	}

	function fileExists(otherId) {
		return files.value.some(({ id }) => id === otherId)
	}

	function removeFile(file) {

		// Removing the last one returns the UI to its opening state.
		if (files.value.length < 2) {
			uiStore.$reset()	
			const previewer = document.getElementById("fetchedUrlPreview")
			removeAllChildNodes(previewer)

		}

		// Taking a picture off the stage means it must not follow you to the
		// next tool either. Anything else would be a removal that did not
		// remove.
		drop()

		const index = files.value.indexOf(file)

		if (index > -1) files.value.splice(index, 1)

		// dropping back to one file returns to the single-image flow
		uiStore.batchMode = files.value.length > 1
	}

	
	function removeAllChildNodes(parent) {
		while (parent.firstChild) {
			parent.removeChild(parent.firstChild);
		}
	}


	// return { files, addFiles, removeFile }
	/* Clip -> frames -> GIF, without either end ever holding the whole thing.
	 *
	 * NOTHING IS UPLOADED HERE. The video is read off the device and stays
	 * there; only the GIF is handed to addFiles(), which is what eventually
	 * submits. The clip never reaches /upload, so the sniffer needs no new
	 * format and no new kind of file is ever stored.
	 *
	 * Every failure is reported in words. A clip that cannot be decoded is the
	 * commonest thing that will go wrong here -- an iPhone's HEVC opened in a
	 * browser without the codec -- and the failure this site has been bitten by
	 * before is the blank screen, where the work had actually succeeded and the
	 * visitor was told nothing at all.
	 */
	/* THE CLIP IS KEPT so the two controls are not dead.
	 *
	 * By the time the GIF is staged the video is gone from the stage, and
	 * changing frames-a-second or width would then change nothing -- which is
	 * the same defect as reoptimize() sending a PDF back as a photograph:
	 * a setting that reads as broken because it silently does nothing. This is
	 * module scope rather than the store deliberately: addFiles() opens with
	 * $reset(), so anything in the store is wiped by the very call that stages
	 * the result.
	 *
	 * Dropped as soon as something arrives that did not come from the
	 * converter, so a 50 MB clip is not held for the rest of the session. */
	/* Open the clip, take a filmstrip, and let the visitor say which part of
	 * it to animate.
	 *
	 * This is where a clip now lands, instead of straight in the converter.
	 * The page used to take the first ten seconds and say so afterwards --
	 * a decision made on somebody's behalf and reported as a fact.
	 *
	 * $reset() FIRST and the strip after, because $reset() clears state() and
	 * the strip lives there: setting it before would put a picture of the
	 * previous clip on this one's trimmer. Same ordering the library pictures
	 * and the truncation notice both need.
	 */
	/* SVG -> PNG, on the device.
	 *
	 * No progress panel and no busy flag: rasterising is one drawImage and a
	 * toBlob, measured in milliseconds, where a clip is seconds of seeking per
	 * frame. A panel that flashes is worse than no panel.
	 *
	 * The warnings are written AFTER addFiles(), which opens with $reset() --
	 * the same ordering the range and the caption need, and the third time
	 * this hazard has been paid for on this path. */
	/* HEIC -> JPEG, one file at a time, saying so as it goes.
	 *
	 * SEQUENTIAL, NOT PARALLEL. Each decode holds a full-resolution RGBA
	 * buffer -- a 12-megapixel photograph is about 48 MB before it is drawn
	 * anywhere -- so twenty at once is a way to run a phone out of memory.
	 * One at a time is slower and finishes.
	 *
	 * The flags are written BEFORE addFiles(), which opens with $reset(), and
	 * the progress is cleared afterwards. Anything that must survive staging
	 * goes after it; the same ordering the range, the caption and the SVG
	 * notices all needed. */
	/* `want` is read HERE, while the store still holds the answer.
	 *
	 * addFiles() opens with $reset(), so batchCapacity() asked for inside the
	 * re-entry would be state()'s default rather than this page's. Same rule
	 * convertVideo() paid for three times -- gather before, never read after.
	 *
	 * It also bounds the work: only as many entries are inflated as the page
	 * could use, so a sixty-picture archive on a twenty-file page decompresses
	 * twenty. The COUNT still has to be honest, which is what `found` is for --
	 * "60 images selected. Taking the first 20" is the true sentence, and
	 * reporting what was extracted would say twenty out of twenty.
	 */
	async function expandZips(archives, others) {
		uiStore.zipBusy = true
		uiStore.zipError = ""
		const want = batchCapacity()

		const made = []
		let found = 0, skipped = 0
		try {
			for (const archive of archives) {
				const out = await expandZip(archive, Math.max(0, want - made.length))
				made.push(...out.files)
				found += out.found
				skipped += out.skipped
			}
		} catch (err) {
			const why = err && err.message
			uiStore.zipBusy = false
			/* Two failures worth telling apart, and NEITHER of them is "doesn't
			 * look like a valid image file" -- which is what both used to say and
			 * is the report this whole path came from. One is a file that is not
			 * an archive; the other is an archive that opened and held no
			 * pictures, which is a thing somebody can act on. */
			uiStore.zipError = t(why === "no-images" ? "errors.zipNoImages" : "errors.zipNotArchive")
			track("optimization_error", {
				error_type: why === "no-images" ? "zip_no_images" : "zip_not_archive",
				input_format: "zip",
				file_count: archives.length,
			})
			return
		}

		uiStore.zipBusy = false

		/* EVERY ENTRY SKIPPED FOR SIZE. The archive held pictures -- `found`
		 * says so -- and not one of them is under the upload ceiling, so there
		 * is nothing to stage and "no pictures in it" would be a lie. Say what
		 * was actually skipped and stop, rather than calling addFiles() with an
		 * empty list and leaving the page silent. */
		if (!made.length) {
			track("optimization_error", {error_type: "some_files_skipped",
				input_format: "zip", file_count: skipped})
			alert(t("errors.skippedNonImages", {n: skipped || found}))
			return
		}

		/* A PAGE THAT TAKES ONE FILE REFUSES A ZIP OF FIVE, which is the rule
		 * addFiles() already applies to five loose photographs and is applied
		 * here because this is the only place that can see how many there were.
		 *
		 * The distinction it keeps is the one recorded above the `cap === 1`
		 * branch: twenty of thirty is about QUANTITY, so taking some and saying
		 * so loses nothing, while one of five is about IDENTITY -- choosing for
		 * the visitor means choosing WRONG most of the time. An archive does not
		 * change that; it only hides it, since without this the page would crop
		 * whichever picture the archive happened to list first and report it as
		 * "taking the first 1". */
		if (want === 1 && found > 1) {
			track("optimization_error", {error_type: "too_many_for_this_page",
				input_format: "zip", file_count: found})
			alert(t("errors.onlyOne", {count: found}))
			return
		}

		/* SAID HERE RATHER THAN ON THE WAY BACK IN, because by then the number
		 * is wrong. addFiles() warns when what it is OFFERED exceeds the cap,
		 * and it is offered exactly `want` -- the other forty never became
		 * files. This is the only place that knows how many were in there. */
		if (found > made.length) {
			track("optimization_error", {error_type: "over_batch_cap",
				input_format: "zip", file_count: found})
			alert(t("errors.tooManyFiles", {count: found, max: made.length}))
		}
		if (skipped) alert(t("errors.skippedNonImages", {n: skipped}))

		// anything that was not an archive travels with them rather than being
		// silently dropped -- a zip alongside two loose photographs is a normal
		// thing to do, and it is the rule convertHeic() already follows
		fromConverter = true
		addFiles(made.concat(others || []))
	}

	async function convertHeic(shots, others) {
		uiStore.heicBusy = true
		uiStore.heicTotal = shots.length
		uiStore.heicDone = 0
		uiStore.heicPhase = "loading"
		uiStore.heicError = ""

		const made = []
		try {
			for (const shot of shots) {
				const out = await heicToJpeg(shot, {
					onProgress: (phase) => { uiStore.heicPhase = phase },
				})
				made.push(out.file)
				uiStore.heicDone = made.length
			}
		} catch (err) {
			const why = err && err.message
			uiStore.heicBusy = false
			uiStore.heicPhase = ""
			/* Three failures worth telling apart. The decoder not arriving is
			 * a network problem and retrying may work; a file libheif cannot
			 * read is a damaged or unusual HEIC; anything else is ours. */
			uiStore.heicError = t(
				why === "decoder-unavailable" ? "errors.heicDecoder"
				: why === "unreadable" ? "errors.heicUnreadable"
				: "errors.heicFailed")
			/* The same three causes the sentence distinguishes. A lot of
			 * decoder-unavailable is a delivery problem and ours to fix; a lot
			 * of unreadable is a format assumption that is wrong. */
			track("optimization_error", {
				error_type: why === "decoder-unavailable" ? "heic_decoder_unavailable"
					: why === "unreadable" ? "heic_unreadable" : "heic_failed",
				input_format: "heic",
				file_count: shots.length,
			})
			return
		}

		uiStore.heicBusy = false
		uiStore.heicPhase = ""
		/* Remember which photograph each JPEG came out of, BEFORE staging:
		 * the JPEG libheif writes carries none of the original's metadata,
		 * and /inspect-metadata has to read the file the visitor actually
		 * chose. Paired by position, which is safe because made[] is built
		 * by pushing one output per input in order. */
		for (let i = 0; i < made.length; i++) {
			if (shots[i]) originals.set(made[i], shots[i])
		}
		// anything that was not a HEIC travels with them rather than being
		// silently dropped -- a mixed drop is a normal thing to do
		fromConverter = true
		addFiles(made.concat(others || []))
	}

	/* `background` is an ARGUMENT, never a store read.
	 *
	 * addFiles() below opens with $reset(), so anything this function read
	 * from the store AFTERWARDS would be state()'s default rather than the
	 * visitor's choice. That is the bug convertVideo() paid for three times
	 * -- the range, the caption, and the width and frame rate silently -- and
	 * taking it as an argument is what makes the whole class impossible.
	 * Callers gather while the store still holds the answer. */
	async function convertSvg(drawing, background, others, width) {
		try {
			const out = await rasteriseSvg(drawing, {background, width})
			fromConverter = true
			addFiles([out.file].concat(others || []))
			lastDrawing = drawing
			lastBackground = background || ""
			lastSvgWidth = width || 0
			/* AFTER staging, all five, because the $reset() inside addFiles()
			 * has just run. Same ordering lastClip and the caption need. */
			uiStore.svgHadText = out.warnings.text
			uiStore.svgHadExternal = out.warnings.external
			uiStore.svgBackground = background || ""
			uiStore.svgWidth = width || 0
			uiStore.svgConverted = true
		} catch (err) {
			/* rasteriseSvg throws "unreadable" for a file <img> would not
			 * parse, and that is the only detail there is -- an <img> reports
			 * no parse error whatsoever. Anything else is a canvas failure. */
			uiStore.svgError = t(err && err.message === "unreadable"
				? "errors.svgUnreadable" : "errors.svgFailed")
		}
	}

	async function startTrim(clip) {
		uiStore.$reset()
		uiStore.videoError = ""
		uiStore.videoBusy = true
		uiStore.videoPhase = "reading"
		uiStore.videoName = clip.name || ""
		try {
			const strip = await sampleStrip(clip)
			lastClip = clip
			lastRange = {from: 0, to: strip.duration}
			uiStore.videoStrip = strip.thumbs
			uiStore.videoDuration = strip.duration
			uiStore.videoStart = 0
			/* Opens on the whole clip rather than on the first ten seconds.
			 * A selection wider than the cap is sampled and sped up, not
			 * trimmed, so the honest default is "all of it" -- and it shows
			 * the speed-up line immediately, which is how the visitor learns
			 * the handles are worth touching. */
			uiStore.videoEnd = strip.duration
			uiStore.videoTrimming = true
		} catch (e) {
			const why = String((e && e.message) || e)
			const undecodable = /undecodable|no picture|no length/.test(why)
			uiStore.videoError = undecodable
				? t("video.undecodable")
				: /cancelled/.test(why) ? "" : t("video.failed")
			/* A cancel is not a failure and is not reported. "Undecodable" is
			 * almost always HEVC off an iPhone in a browser without the
			 * codec, which is the single most useful thing this page could
			 * learn about its own traffic. */
			if (!/cancelled/.test(why)) {
				track("optimization_error", {
					error_type: undecodable ? "video_undecodable" : "video_failed",
					input_format: "video",
				})
			}
		} finally {
			uiStore.videoBusy = false
			uiStore.videoPhase = ""
		}
	}

	/* One scratch canvas for the whole run, not one per frame.
	 *
	 * The frames arrive as ImageData from the decoder's own canvas; drawing
	 * text needs a context, so they are put back onto a canvas of the same
	 * size, captioned, and read out again. Allocating that canvas per frame is
	 * a hundred allocations of a megabyte on a ten-second clip. */
	let capCanvas = null
	function captioned(frame, meme) {
		if (!capCanvas) capCanvas = document.createElement("canvas")
		if (capCanvas.width !== frame.width || capCanvas.height !== frame.height) {
			capCanvas.width = frame.width
			capCanvas.height = frame.height
		}
		const ctx = capCanvas.getContext("2d", {willReadFrequently: true})
		ctx.putImageData(frame, 0, 0)
		drawMeme(ctx, meme, frame.width, frame.height)
		return ctx.getImageData(0, 0, frame.width, frame.height)
	}

	/* Put the trimmer away and forget the clip. The visitor asked for a
	 * different one, so holding 50 MB of this one is not a convenience. */
	function cancelTrim() {
		lastClip = null
		lastRange = null
		lastSignature = null
		bakedCaption = null
		uiStore.$reset()
	}

	/* EVERY SETTING ARRIVES AS AN ARGUMENT, and that is the point.
	 *
	 * This function opens with $reset(), which puts state() back -- so
	 * anything it read out of the store afterwards was the DEFAULT, not what
	 * the visitor chose. The range went that way first and was caught; the
	 * caption went the same way and produced a re-conversion with no words in
	 * it; and videoWidth/videoFps were reading 480/10 whatever the selects
	 * said, which nothing caught because the defaults happen to match.
	 *
	 * Taking them as an argument makes the whole class of bug impossible:
	 * there is nothing here to read late. Callers gather from the store while
	 * it still holds the answer. */
	async function convertVideo(clip, settings) {
		const {from = 0, to = null, caption = null, bake = false, zones = 1,
			width = DEFAULT_WIDTH, fps = DEFAULT_FPS} = settings || {}
		uiStore.$reset()
		uiStore.videoError = ""
		uiStore.videoBusy = true
		uiStore.videoPhase = "reading"
		uiStore.videoDone = 0
		uiStore.videoTotal = 0
		uiStore.videoName = clip.name || ""

		let sink = null
		try {
			/* THE WORDS ARE ONLY DRAWN WHEN ASKED, and a preview does not ask.
			 *
			 * A conversion for the stage leaves them out, so WatermarkLayer
			 * can draw them in CSS and CropSurface's rectangle can be dragged
			 * with the words re-fitting on every pointer move -- free, because
			 * nothing is re-encoded. Baking them into the preview instead
			 * would mean the words could not move until a conversion finished,
			 * which is precisely the "the text doesn't seem to automatically
			 * adjust" complaint the meme rectangles were built to answer.
			 *
			 * Save asks. See bakeVideoCaption().
			 *
			 * usableMeme() returns null for empty text, so an untouched field
			 * costs no canvas pass per frame. Only the bottom slot: two bands
			 * on a 180-pixel-tall GIF leave no picture between them. */
			const meme = bake && caption ? usableMeme(caption) : null

			/* Worked out BEFORE the first frame, because the encoder is built
			 * with it -- and from the same function planFrames() uses, so the
			 * delay the frames are placed by and the delay they are written
			 * with cannot differ. */
			const {delayCs} = delayForFps(fps)
			const info = await decodeFrames(clip, {
				targetWidth: width, fps,
				maxSeconds: MAX_SECONDS,
				start: from,
				end: to,
			}, (frame, i, total) => {
				/* The sink is built on the FIRST frame, not before the loop:
				 * only decodeFrames() knows the size it settled on, after
				 * fitWithin() has held a portrait clip to its height. */
				if (!sink) {
					sink = createGifSink({
						width: frame.width, height: frame.height,
						// the same delay the plan placed the frames with, so
						// the encoded running time is the one that was shown
						delayCs,
					})
					uiStore.videoPhase = "converting"
					uiStore.videoTotal = total
				}
				sink.add(meme ? captioned(frame, meme) : frame)
				uiStore.videoDone = i + 1
			})

			if (!sink) throw new Error("no frames came out of this clip")

			uiStore.videoPhase = "assembling"
			const bytes = sink.finish()
			const file = new File([bytes], gifNameFor(clip.name),
				{type: "image/gif", lastModified: Date.now()})

			uiStore.videoBusy = false
			uiStore.videoPhase = ""
			fromConverter = true
			addFiles([file])

			/* Said AFTER the GIF is staged, because $reset() inside addFiles()
			 * clears it -- the same ordering the library pictures need. And
			 * addFiles() forgets the clip, so put it back: it is what the two
			 * controls re-run against. */
			/* Put back what staging wiped. addFiles() opens with $reset(), so
			 * the words and their rectangle -- which live in the store because
			 * that is where the preview and the drag read them -- are gone by
			 * the time this line runs. Same ordering the range needs, and the
			 * reason every SETTING is an argument rather than a store read. */
			lastClip = clip
			lastRange = {from: info.from, to: info.to}
			if (caption) {
				/* EVERYTHING MemeControl SETS, not only the words.
				 *
				 * $reset() puts the whole panel back to the task's preset, so
				 * restoring the text alone left a caption the visitor had
				 * made yellow coming back white, at the preset size, in the
				 * preset face -- silently, on every change of frame rate. */
				uiStore.memeZones = zones
				/* EVERY AREA, walked rather than named. Restoring only top and
				 * bottom wiped areas 3 and 4 on every re-conversion -- change
				 * the frame rate and two captions vanish with nothing said. */
				for (const slot of MEME_SLOTS) {
					const k = memeStoreKeys(slot)
					uiStore[k.text] = caption[slot] || ""
					uiStore[k.box] = caption[`${slot}Box`] || null
					if (caption[`${slot}Color`]) uiStore[k.color] = caption[`${slot}Color`]
					if (caption[`${slot}Size`]) uiStore[k.size] = caption[`${slot}Size`]
				}
				if (caption.font) uiStore.memeFont = caption.font
				uiStore.memeUpper = !!caption.upper
			}
			lastSignature = conversionSignature()
			bakedCaption = bake ? JSON.stringify(caption) : null
			uiStore.videoFrames = info.frames
			uiStore.videoSeconds = info.seconds
			uiStore.videoSpeed = info.speed
			uiStore.videoSped = info.sped
		} catch (e) {
			const why = String((e && e.message) || e)
			uiStore.videoBusy = false
			uiStore.videoPhase = ""
			const undecodable = /undecodable|no picture|no length/.test(why)
			uiStore.videoError = undecodable
				? t("video.undecodable")
				: /cancelled/.test(why) ? "" : t("video.failed")
			/* A cancel is not a failure and is not reported. "Undecodable" is
			 * almost always HEVC off an iPhone in a browser without the
			 * codec, which is the single most useful thing this page could
			 * learn about its own traffic. */
			if (!/cancelled/.test(why)) {
				track("optimization_error", {
					error_type: undecodable ? "video_undecodable" : "video_failed",
					input_format: "video",
				})
			}
		}
	}

	/* What the conversion itself depends on.
	 *
	 * Everything else in the options panel is a directive on the finished GIF
	 * -- format, quality, rotation, and now the crop -- and a directive costs
	 * a rewritten URL. These four cost decoding and re-encoding the whole
	 * clip, which is seconds.
	 *
	 * The crop is deliberately ABSENT: it is a `cr_` directive for an animated
	 * GIF, so cropping must not re-convert. That was the reported problem. */
	/* NUMBERS, COERCED HERE.
	 *
	 * A <select> hands back a STRING and OptionsPanel's set() only coerces
	 * `pct`, so the store held "480" after a touch and 480 before one. The
	 * signature is JSON, and JSON.stringify(["480",...]) is not
	 * JSON.stringify([480,...]) -- so re-picking the SAME width re-decoded
	 * the whole clip. Reported as "the UI starts processing again before I
	 * have selected any value", which is what it looks like: opening the
	 * select and closing it is enough on the platforms that fire change.
	 *
	 * Coerced at this one boundary rather than in set(): everything the
	 * conversion runs on and everything the signature compares comes through
	 * here, so there is nowhere else for a string to get in. */
	function currentSettings() {
		return {
			from: Number(lastRange ? lastRange.from : 0) || 0,
			to: lastRange && lastRange.to != null ? Number(lastRange.to) : null,
			/* EVERYTHING MemeControl SETS, not just the bottom text.
			 *
			 * It offers four areas with a colour and a size each, a shared
			 * face and capitals -- and usableMeme() takes exactly this shape,
			 * so the conversion draws what the panel shows with no field left
			 * behind. A caption of only `bottom` silently dropped the other
			 * areas, the colours and the face the moment the control appeared.
			 *
			 * memePayload() rather than the object written out here: the
			 * download pass in App.vue needs the identical shape, and two
			 * hand-written copies of sixteen fields is how one of them comes
			 * to forget an area. It decides the closed-area rule itself, so
			 * there is no slot list to keep in step here. */
			caption: memePayload(uiStore, uiStore.memeZones),
			/* HOW MANY AREAS ARE OPEN, carried beside the words and not
			 * inside them.
			 *
			 * Beside, because bakedCaption compares the caption object to
			 * decide whether Save has to run the clip again -- and opening an
			 * empty second area changes nothing in the frames, so it must not
			 * cost a re-encode. Carried at all, because $reset() puts this
			 * back to one while the words are restored around it: without it
			 * a re-conversion left the second area's caption on the picture
			 * with no field and no rectangle to reach it. */
			zones: Number(uiStore.memeZones) || 1,
			width: Number(uiStore.videoWidth) || DEFAULT_WIDTH,
			fps: Number(uiStore.videoFps) || DEFAULT_FPS,
		}
	}

	/* What a PREVIEW conversion depends on -- deliberately not the caption.
	 *
	 * The words are not in a preview at all, so typing them and dragging their
	 * rectangle must cost nothing. The crop is absent for the same reason it
	 * was before: it is a cr_ directive. Only these four mean the frames
	 * themselves would come out different. */
	function conversionSignature() {
		const s = currentSettings()
		return JSON.stringify([s.width, s.fps, s.from, s.to])
	}
	let lastSignature = null

	/* Run it again, but ONLY if it would come out different.
	 *
	 * Returns false when there is nothing to run or nothing has changed, so
	 * reoptimize() falls through to the ordinary re-optimise -- which is a new
	 * URL and no work. Without this every touch of any control re-decoded the
	 * clip: choosing PNG, nudging quality, or dragging a crop rectangle all
	 * spent seconds redoing frames that were already correct. */
	/* Re-rasterise the drawing behind a new background.
	 *
	 * Only the background can send us back here. The width, the quality and
	 * the output format are all directives the CDN applies to the PNG
	 * afterwards; this is the one SVG setting baked into the bytes, which is
	 * exactly why the original drawing has to be kept.
	 *
	 * Returns true when it took the work, so reoptimize() knows to stop --
	 * the same contract reconvertVideo() has. */
	function reconvertSvg() {
		if (!lastDrawing) return false
		const wanted = uiStore.svgBackground || ""
		const px = uiStore.svgWidth || 0
		/* BOTH, because both are baked into the PNG. The width was added
		 * second and only the background was compared, which would have made
		 * the new control silently do nothing -- the same shape as the
		 * reoptimize() bug where every control re-decoded a clip, in reverse. */
		if (wanted === lastBackground && px === lastSvgWidth) return false
		convertSvg(lastDrawing, wanted, [], px)
		return true
	}

	function reconvertVideo() {
		if (!lastClip) return false
		const now = conversionSignature()
		if (now === lastSignature) return false
		convertVideo(lastClip, currentSettings())
		return true
	}

	/* Burn the words in, just before the file is handed over.
	 *
	 * Returns true when it did something, so the caller knows to skip the
	 * ordinary canvas pass -- which would flatten the animation, a canvas
	 * holding one frame.
	 *
	 * Nothing to do when there are no words, or when the bytes already carry
	 * exactly these ones: pressing Save twice must not re-encode the clip
	 * twice for the same answer. */
	async function bakeVideoCaption() {
		if (!lastClip) return false
		const settings = currentSettings()
		const words = String(settings.caption.top || "") + String(settings.caption.bottom || "")
		if (!words.trim()) return false
		const wanted = JSON.stringify(settings.caption)
		if (bakedCaption === wanted) return false
		await convertVideo(lastClip, Object.assign(settings, {bake: true}))
		return true
	}

	/* What the trimmer's button presses.
	 *
	 * The selection is copied out of the store HERE, because that is the last
	 * moment it exists: convertVideo() opens with $reset(). The handles write
	 * to the store on every pointer move -- no local copy, for the reason the
	 * caption rectangles record -- so this is the hand-off between the live
	 * value and the one the conversion actually runs on. */
	function convertTrimmed() {
		if (!lastClip || uiStore.videoBusy) return
		lastRange = {from: uiStore.videoStart || 0,
			to: uiStore.videoEnd || uiStore.videoDuration || null}
		const settings = currentSettings()
		uiStore.videoTrimming = false
		convertVideo(lastClip, settings)
	}

	return {
		files, addFiles, removeFile, removeAllChildNodes,
		convertVideo, reconvertVideo, convertTrimmed, cancelTrim,
		bakeVideoCaption,
		convertSvg,
		reconvertSvg,
		convertHeic,
	}
}

/* How the NEXT intake arrived.
 *
 * A setter rather than an argument to addFiles(), because the drop zone binds
 * addFiles directly to its event (`@files-dropped="addFiles"`) and the picker
 * hands it an event target's FileList. Giving addFiles a second parameter
 * would mean a wrapper at every one of the nine call sites and a template
 * change; this is one call immediately before the existing one.
 *
 * It survives exactly one addFiles() and is then cleared, so a carry-over or
 * a sample arriving later cannot wear the label of a gesture that has already
 * been counted.
 */
export function enteredBy(how) {
	entryMethod = how
}

class UploadableFile {
	constructor(file) {
		this.file = file
		this.name = file.name
		this.size = file.size
		this.width = 0
		this.height = 0
		this.id = `${file.name}-${file.size}-${file.lastModified}-${file.type}`
		this.url = URL.createObjectURL(file)
		this.status = null
	}
}
